import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { DESTINATIONS, QUOTE_WA, buildWhatsAppLink } from "../lib/constants";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758717152007-6a2eb7299409?w=1800&q=80";

export default function Destinations() {
  return (
    <div data-testid="destinations-page">
      <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Maldives overwater villas" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/75 to-[#05070C]/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070C]/30 via-transparent to-[#05070C]" />
        </div>
        <div className="relative container-x pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="eyebrow mb-6">
              <span className="hairline" /> Destinations
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Inspiration for the <span className="gold-text italic">well-travelled.</span>
            </h1>
            <p className="mt-6 max-w-xl text-white/65">
              From boardrooms in Dubai to overwater villas in the Maldives — our
              most-requested destinations, pre-vetted by the team.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DESTINATIONS.map((d, i) => (
              <motion.article
                key={d.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                data-testid={`destination-tile-${d.id}`}
                className="group relative overflow-hidden"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={d.image}
                    alt={d.name}
                    className="h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05070C] via-[#05070C]/30 to-transparent" />
                  <div className="absolute top-5 left-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold/90">
                    <MapPin className="h-3 w-3" />
                    {d.region}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <h3 className="font-display text-3xl text-white">{d.name}</h3>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed">
                      {d.blurb}
                    </p>
                    <div className="mt-5 flex items-center gap-2 text-gold text-xs uppercase tracking-[0.22em] opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-500">
                      <a
                        href={buildWhatsAppLink(
                          `Hello Beyond Borders Travel, I'd like to enquire about travel to ${d.name} (${d.region}). Please send me a quote.`
                        )}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`destination-enquire-${d.id}`}
                      >
                        Enquire on WhatsApp
                      </a>
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#0B1120]/40 border-t border-white/5">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl lg:text-5xl text-white">
            Don't see it? <span className="gold-text">We've been there.</span>
          </h2>
          <p className="mt-6 text-white/65">
            Our consultants design itineraries to over 180 countries. Tell us
            where business — or pleasure — is taking you next.
          </p>
          <a
            href={QUOTE_WA}
            target="_blank"
            rel="noreferrer"
            data-testid="destinations-cta-quote"
            className="btn-gold mt-10"
          >
            Plan with us on WhatsApp
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>
    </div>
  );
}
